import React, { useState } from 'react';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import {
  Box,
  Button,
  Card,
  Typography,
  Avatar,
  ButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Divider,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import HotTubIcon from '@mui/icons-material/HotTub';
import AddIcon from '@mui/icons-material/Add';
import SpaIcon from '@mui/icons-material/Spa';
import { StatusMember, DeleteJoinMember } from "../Apihelper/MemberApiHelpr";
import { CreateSteambath, UseSteambath } from "../Apihelper/SteambathHelper";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
  },
  background: '#ffffff',
  height: '320px',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  width: '100%'
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: '20px',
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  gap: '16px'
}));

const CardContent = styled(Box)({
  padding: '20px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
});

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiSvgIcon-root': {
    color: '#64748b',
    fontSize: '20px'
  }
});

const CardActions = styled(Box)({
  padding: '12px 20px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#f8fafc'
});

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
  padding: '6px 16px',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

const StatusBadge = styled(Box)(({ status }) => ({
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: status === 1 ? '#dcfce7' : '#fee2e2',
  color: status === 1 ? '#059669' : '#dc2626',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: status === 1 ? '#bbf7d0' : '#fecaca',
  }
}));

const SteambathBadge = styled(Box)(({ available }) => ({
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: available === "Yes" ? '#dcfce7' : '#fee2e2',
  color: available === "Yes" ? '#059669' : '#dc2626',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
}));

const SteambathCount = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  backgroundColor: 'rgba(0,0,0,0.1)',
  padding: '2px 6px',
  borderRadius: '12px',
  marginLeft: '4px'
});

function MemberTileView({ members, onLoadMore, hasMore, onStatusChange, onDelete, isLoading }) {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [isActive, setIsActive] = useState(false);
  
  // Add steambath states
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [steambath, setOpensteambath] = useState(false);
  const [steambathvalue, setsteambathValue] = useState('');
  const [steambathquantity, setSteambathQuantity] = useState("");
  const [steambathPaymentDate, setSteambathPaymentDate] = useState(null);
  const parsePaymentDateToDayjs = (paymentDate) => {
    if (paymentDate == null || paymentDate === "") return dayjs();
    const s = String(paymentDate).trim();
    const parts = s.split(/[-/]/);
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (!isNaN(d) && !isNaN(m) && !isNaN(y) && y > 31) {
        return dayjs(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
      }
    }
    const parsed = dayjs(s);
    return parsed.isValid() ? parsed : dayjs();
  };

  // Add steambath handlers
  const handleOpensteambath = (memberId, paymentDate) => {
    setSelectedMemberId(memberId);
    setSteambathPaymentDate(parsePaymentDateToDayjs(paymentDate));
    setOpensteambath(true);
  };

  const handleClosesteambath = () => {
    setSteambathQuantity("");
    setsteambathValue("");
    setSteambathPaymentDate(null);
    setSelectedMemberId(null);
    setOpensteambath(false);
  };

  const handleChangeSteambathQuantity = (event) => {
    const inputValue = event.target.value;
    if (/^\d*$/.test(inputValue)) {
      setSteambathQuantity(inputValue);
    }
  };

  const handleChangeSteambathValue = (event) => {
    const inputValue = event.target.value;
    if (/^\d*$/.test(inputValue)) {
      setsteambathValue(inputValue);
    }
  };

  const handleDeleteClick = (member) => {
    setSelectedMember(member);
    setOpenDeleteDialog(true);
  };

  const handleStatusClick = (member) => {
    setSelectedMember(member);
    setIsActive(member.status === 1);
    setOpenStatusDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;

      const res = await DeleteJoinMember(userToken, userId, selectedMember.id);

      if (res.code === "200") {
        onDelete();
        setOpenDeleteDialog(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleConfirmStatus = async () => {
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const newStatus = selectedMember.status === 1 ? 0 : 1;

      const res = await StatusMember(userToken, userId, newStatus, selectedMember.id);

      if (res.code == "200") {
        onStatusChange();
        setOpenStatusDialog(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewDetails = (memberId) => {
    navigate('/memberdetails', {
      state: {
        id: memberId,
        random_no: Math.random(),
      },
    });
  };

  const handleSubmitSteambath = async () => {
    if (!selectedMemberId) {
      console.error("No member ID selected");
      return;
    }

    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const UseradminData = {
        member_id: selectedMemberId,
        quantity: steambathquantity,
        amount: steambathvalue,
        payment_date: steambathPaymentDate?.isValid()
          ? steambathPaymentDate.format("DD-MM-YYYY")
          : dayjs().format("DD-MM-YYYY"),
      };

      const response = await CreateSteambath(userToken, UseradminData);
      
      if (response.code === 200) {
        handleClosesteambath();
        onStatusChange(); // Refresh the member list
      } else {
        console.error("Failed to add steam bath:", response.message);
      }
    } catch (error) {
      console.error("API Error:", error.message);
    }
  };

  const handleUseBath = async (id) => {
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const userId = userDataObj.id;
      const UseradminData = {
        member_id: id,
      };

      const response = await UseSteambath(userToken, UseradminData);
      
      if (response.code === 200) {
        onStatusChange(); // Refresh the member list
      } else {
        console.error("Failed to use steam bath:", response.message);
      }
    } catch (error) {
      console.error("API Error:", error.message);
    }
  };

  return (
  <>
      <Grid 
        container 
        spacing={2}
        sx={{
          width: '100%',
          margin: 0,
          '& > .MuiGrid-item': {
            padding: '8px'
          }
        }}
      >
        {members.map((member) => (
          <Grid 
            item 
            xs={12} 
            sm={6} 
            md={3} 
            key={member.id}
            sx={{
              width: {
                xs: '100%',
                sm: '50%',
                md: '32%'
              },
              flexBasis: {
                xs: '100%',
                sm: '50%',
                md: '32%'
              },
              maxWidth: {
                xs: '100%',
                sm: '50%',
                md: '32%'
              }
            }}
          >
            <StyledCard>
              <CardHeader>
                <Avatar
                  src={member.image}
                  alt={member.name}
                  sx={{
                    width: 64,
                    height: 64,
                    border: '3px solid #ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#ffffff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => handleViewDetails(member.id)}
                  >
                    {member.name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleViewDetails(member.id)}
                  >
                    ID: {member.membership_number}
                  </Typography>
                </Box>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <PhoneIcon />
                  <Typography variant="body2">{member.phone}</Typography>
                </InfoRow>
                <InfoRow>
                  <EmailIcon />
                  <Typography variant="body2">{member.email}</Typography>
                </InfoRow>
                <InfoRow>
                  <BadgeIcon />
                  <Typography variant="body2">Member since: {new Date(member.created_at).toLocaleDateString()}</Typography>
                </InfoRow>
                <InfoRow>
                  <HotTubIcon />
                  <SteambathBadge available={member.bath_available}>
                    {member.bath_available === "Yes" ? "Steam Bath Available" : "No Steam Bath"}
                    {member.bath_available === "Yes" && member.total_bath > 0 && (
                      <SteambathCount>
                        {member.total_bath} left
                      </SteambathCount>
                    )}
                  </SteambathBadge>
                </InfoRow>
              </CardContent>

              <CardActions>
                <StatusBadge 
                  status={member.status}
                  onClick={() => handleStatusClick(member)}
                >
                  {member.status === 1 ? 'Active' : 'Inactive'}
                </StatusBadge>
                <ButtonGroup variant="outlined" size="small">
                  <Tooltip title="View Details">
                    <ActionButton
                      onClick={() => handleViewDetails(member.id)}
                      sx={{ 
                        color: appColors.evergreen, 
                        borderColor: appColors.evergreen,
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title={member.bath_available === "Yes" ? "Use Bath" : "Add Steam Bath"}>
                    <ActionButton
                      onClick={() =>
                        member.bath_available === "Yes"
                          ? handleUseBath(member.id)
                          : handleOpensteambath(
                              member.id,
                              member.bath_payment_date ?? member.payment_date ?? member.steam_bath_payment_date
                            )
                      }
                      sx={{ 
                        color: '#059669', 
                        borderColor: '#059669',
                      }}
                    >
                      {member.bath_available === "Yes" ? <AddIcon fontSize="small" /> : <SpaIcon fontSize="small" />}
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <ActionButton
                      onClick={() => handleDeleteClick(member)}
                      sx={{ 
                        color: '#dc2626', 
                        borderColor: '#dc2626',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </ActionButton>
                  </Tooltip>
                </ButtonGroup>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={onLoadMore}
            sx={{
              backgroundColor: appColors.evergreen,
              '&:hover': { backgroundColor: appColors.pine },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 4,
              py: 1
            }}
          >
            Load More
          </Button>
        </Box>
      )}

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this member? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {isActive ? 'deactivate' : 'activate'} this member?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmStatus} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Steambath Dialog */}
      <Dialog 
        open={steambath} 
        onClose={handleClosesteambath}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: appColors.evergreen, fontWeight: 'bold' }}>
          Add Steambath
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Payment date"
              value={steambathPaymentDate}
              onChange={(v) => setSteambathPaymentDate(v)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  margin: "dense",
                  fullWidth: true,
                  sx: { mb: 1 },
                },
              }}
            />
          </LocalizationProvider>
          <TextField
            autoFocus
            margin="dense"
            label="Quantity"
            type="text"
            fullWidth
            variant="outlined"
            value={steambathquantity}
            onChange={handleChangeSteambathQuantity}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: appColors.evergreen,
                },
                '&.Mui-focused fieldset': {
                  borderColor: appColors.evergreen,
                },
              }
            }}
          />
          <TextField
            margin="dense"
            label="Amount"
            type="text"
            fullWidth
            variant="outlined"
            value={steambathvalue}
            onChange={handleChangeSteambathValue}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: appColors.evergreen,
                },
                '&.Mui-focused fieldset': {
                  borderColor: appColors.evergreen,
                },
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClosesteambath}
            sx={{
              color: '#64748b',
              '&:hover': {
                backgroundColor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitSteambath}
            variant="contained"
            sx={{
              backgroundColor: appColors.evergreen,
              '&:hover': {
                backgroundColor: appColors.pine
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
 </>
  );
}

export default MemberTileView; 